export const LOCALE_STORAGE_KEY = 'rv_locale';

export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English', nativeLabel: 'English', flag: '🇬🇧', dir: 'ltr' as const },
  { code: 'es', label: 'Spanish', nativeLabel: 'Español', flag: '🇪🇸', dir: 'ltr' as const },
  { code: 'fr', label: 'French', nativeLabel: 'Français', flag: '🇫🇷', dir: 'ltr' as const },
  { code: 'cy', label: 'Welsh', nativeLabel: 'Cymraeg', flag: '🏴󠁧󠁢󠁷󠁬󠁳󠁿', dir: 'ltr' as const },
  { code: 'it', label: 'Italian', nativeLabel: 'Italiano', flag: '🇮🇹', dir: 'ltr' as const },
  { code: 'de', label: 'German', nativeLabel: 'Deutsch', flag: '🇩🇪', dir: 'ltr' as const },
  { code: 'ar', label: 'Arabic', nativeLabel: 'العربية', flag: '🇸🇦', dir: 'rtl' as const },
] as const;

export type AppLanguageCode = (typeof SUPPORTED_LANGUAGES)[number]['code'];

export function isAppLanguageCode(value: string): value is AppLanguageCode {
  return SUPPORTED_LANGUAGES.some((lang) => lang.code === value);
}

export function getLanguageMeta(code: string) {
  return SUPPORTED_LANGUAGES.find((lang) => lang.code === code) ?? SUPPORTED_LANGUAGES[0];
}

/**
 * Apply language to the document. Layout stays LTR so chrome/login do not
 * flip when Arabic is selected; translated copy still updates via i18n.
 * Always opt out of browser auto-translate — the app owns locale switching.
 */
export function applyDocumentLanguage(code: string) {
  if (typeof document === 'undefined') return;
  const meta = getLanguageMeta(code);
  const root = document.documentElement;
  root.lang = meta.code;
  root.dir = 'ltr';
  root.setAttribute('translate', 'no');
  root.classList.add('notranslate');
  if (document.body) {
    document.body.setAttribute('translate', 'no');
    document.body.classList.add('notranslate');
  }
}

/**
 * Best matching supported language from the browser's preferred languages
 * (`navigator.languages`, then `navigator.language`). Region tags are ignored
 * (`es-MX` → `es`, `en-GB` → `en`).
 */
export function detectBrowserLanguage(): AppLanguageCode | null {
  if (typeof navigator === 'undefined') return null;
  const candidates: string[] = [];
  try {
    if (Array.isArray(navigator.languages)) {
      for (const tag of navigator.languages) {
        if (tag) candidates.push(tag);
      }
    }
  } catch {
    // Ignore.
  }
  try {
    if (navigator.language) candidates.push(navigator.language);
  } catch {
    // Ignore.
  }
  for (const tag of candidates) {
    const primary = String(tag).trim().toLowerCase().split(/[-_]/)[0];
    if (primary && isAppLanguageCode(primary)) return primary;
  }
  return null;
}

/**
 * Resolve UI language: explicit localStorage preference first, then browser
 * language preference, then English. Does not write storage — that happens when
 * the user changes language (or a profile preferred_locale is applied).
 */
export function readStoredLanguage(): AppLanguageCode {
  if (typeof window === 'undefined') return 'en';
  try {
    const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    if (stored && isAppLanguageCode(stored)) return stored;
  } catch {
    // Ignore storage errors.
  }
  return detectBrowserLanguage() ?? 'en';
}
